/*
 * PRD-037 — Modal de contratación del paso de plan del claim (app SEPARADA).
 *
 * En el claim el ÚNICO método de pago ofrecido es TARJETA (la transferencia se oculta a propósito:
 * el dueño recién activado contrata en línea con tarjeta + 3DS). Por eso NO se monta el
 * PaymentMethodSelector compartido del panel.
 *
 * Sin paso de "confirmar método": mostrar una tarjeta que dice "Tarjeta de crédito o débito" como
 * si fuera una elección, cuando es la ÚNICA opción, no aporta nada — el clic en "Elegir <plan>"
 * (ClaimPlanStep) YA es la decisión deliberada del dueño.
 *
 * SÍ hay un paso previo mínimo: capturar un código de descuento OPCIONAL. La suscripción se crea
 * en Stripe al llamar convertFreemiumToPlan, así que el cupón debe viajar EN ESA llamada. Por eso
 * NO se dispara automático al abrir: primero se muestra el campo de código + "Continuar al pago";
 * ese botón dispara convertFreemiumToPlan con el coupon y recién entonces se monta Stripe Elements.
 * El FE no valida el código (contrato: el backend lo resuelve; si no existe, se cobra precio lleno).
 *
 * Reusa los presentacionales del panel (StripePaymentForm / isStripeConfigured) sobre
 * convertFreemiumToPlan (axiosInstance directo). Los errores se muestran inline (el partner no
 * monta <Toaster>).
 */
import { useEffect, useRef, useState } from 'react';
import { Modal } from '@plick/ui';
import { IconLock } from '@tabler/icons-react';
import { V4 } from '@components/v4/tokens';
import { Button } from '@components/v4/Button/Button';
import { StripePaymentForm, isStripeConfigured } from '@pages/Subscriptionsv2/components/StripePaymentForm';
import type { MetodoPago } from '@pages/Subscriptionsv2/hooks/useActivatePlan';
import type { AvailableSubscription } from '@interfaces/general';
import { formatToCurrency } from '@utils/formats';
import { convertFreemiumToPlan } from './useClaimPlans';

// En el claim el método de pago es siempre tarjeta (la transferencia no se ofrece).
const METODO_PAGO: MetodoPago = 'tarjeta';

interface ClaimPlanModalProps {
  plan: AvailableSubscription;
  isOpen: boolean;
  onClose: () => void;
  /** Se invoca tras una contratación exitosa (tarjeta confirmada / 3DS resuelto). */
  onActivated: () => void;
}

export const ClaimPlanModal = ({ plan, isOpen, onClose, onActivated }: ClaimPlanModalProps) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Código de descuento OPCIONAL que teclea el dueño. Viaja en la MISMA llamada que crea la
  // suscripción (convertFreemiumToPlan); por eso se captura ANTES de disparar el pago.
  const [coupon, setCoupon] = useState('');
  // Evita re-disparar convertFreemiumToPlan (crea la suscripción incomplete en Stripe) más de una
  // vez — guard síncrono, no depende del estado async.
  const fetchedRef = useRef(false);

  const cardConfigured = isStripeConfigured();

  const fetchClientSecret = (couponCode?: string) => {
    if (fetchedRef.current || !cardConfigured) return;
    fetchedRef.current = true;
    setError(null);
    setIsLoading(true);
    convertFreemiumToPlan(plan.id, METODO_PAGO, couponCode)
      .then((response) => {
        if (response.client_secret) {
          setClientSecret(response.client_secret);
        } else {
          setError('No se recibió la información de pago. Intenta de nuevo.');
        }
      })
      .catch((err: unknown) => {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (err as Error)?.message ||
          'No se pudo activar el plan. Intenta de nuevo.';
        setError(message);
      })
      .finally(() => setIsLoading(false));
  };

  // Al cerrar el modal reseteamos el flujo para que la próxima apertura empiece en el paso del
  // código. NO se dispara convertFreemium automáticamente: el dueño primero teclea el código
  // (opcional) y presiona "Continuar al pago", que crea la suscripción con el coupon.
  useEffect(() => {
    if (!isOpen) {
      fetchedRef.current = false;
      setClientSecret(null);
      setError(null);
      setCoupon('');
    }
  }, [isOpen, plan.id]);

  const handleClose = () => {
    setClientSecret(null);
    setError(null);
    setCoupon('');
    onClose();
  };

  // Paso "aplicar código (opcional) → continuar": crea la suscripción con el coupon tecleado.
  const handleContinueToPayment = () => {
    fetchClientSecret(coupon);
  };

  const handleRetry = () => {
    fetchedRef.current = false;
    // Reintenta con el mismo código que el dueño ya tecleó.
    fetchClientSecret(coupon);
  };

  return (
    <Modal padding="0rem" maxHeight="90vh" maxWidth="480px" isOpen={isOpen} onClose={handleClose}>
      <div style={{ padding: 24, fontFamily: 'Poppins, sans-serif' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: V4.primary }}>Activar {plan.nombre}</h3>
        {typeof plan.precio === 'number' && (
          <p style={{ margin: '4px 0 16px', fontSize: 13, color: V4.grayUltraDark }}>
            {formatToCurrency(plan.precio)} · {plan.periodo_pago === 'anual' ? 'pago anual' : 'pago mensual'}
          </p>
        )}

        {!cardConfigured && (
          <p style={{ margin: 0, fontSize: 12, color: V4.warningDark, lineHeight: 1.4 }}>
            El pago con tarjeta no está disponible por ahora. Vuelve a intentarlo más tarde o contacta a soporte.
          </p>
        )}

        {/* Paso 1: capturar código de descuento (opcional) antes de crear la suscripción. */}
        {cardConfigured && !clientSecret && !isLoading && !error && (
          <>
            <label
              htmlFor="claim-coupon"
              style={{ display: 'block', margin: '4px 0 6px', fontSize: 13, fontWeight: 600, color: V4.primary }}
            >
              ¿Tienes un código de descuento?
            </label>
            <input
              id="claim-coupon"
              type="text"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value.toUpperCase())}
              placeholder="Código (opcional)"
              autoComplete="off"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                fontSize: 14,
                fontFamily: 'Poppins, sans-serif',
                color: V4.primary,
                background: V4.white,
                border: `1px solid ${V4.grayLight}`,
                borderRadius: 8,
                outline: 'none',
                textTransform: 'uppercase',
              }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleContinueToPayment}>
                Continuar al pago
              </Button>
            </div>
          </>
        )}

        {cardConfigured && isLoading && !clientSecret && (
          <p style={{ margin: 0, fontSize: 13, color: V4.grayUltraDark }}>Preparando tu pago…</p>
        )}

        {cardConfigured && error && !isLoading && (
          <>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: V4.danger, lineHeight: 1.4 }} role="alert">
              {error}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleRetry}>
                Reintentar
              </Button>
            </div>
          </>
        )}

        {cardConfigured && clientSecret && (
          <>
            <p
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                margin: '0 0 16px',
                fontSize: 12,
                color: V4.grayUltraDark,
              }}
            >
              <IconLock size={14} stroke={1.8} />
              Pago seguro procesado por Stripe.
            </p>
            <StripePaymentForm clientSecret={clientSecret} onSuccess={onActivated} onCancel={handleClose} />
          </>
        )}
      </div>
    </Modal>
  );
};
